import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { axios } from "@/admin/libs/axios";

export type FeedbackKind = "GENERAL" | "NPS_FOLLOWUP" | "EXIT_SURVEY" | "BUG_REPORT";
export type FeedbackStatus = "NEW" | "READ" | "REPLIED" | "RESOLVED" | "ARCHIVED";
export type RatingBucket = "detractor" | "passive" | "promoter" | "low" | "high";

export interface AdminFeedbackUser {
  id: string;
  name: string;
  email: string;
  picture: string | null;
}

export interface AdminFeedbackRow {
  id: string;
  userId: string | null;
  email: string | null;
  kind: FeedbackKind;
  status: FeedbackStatus;
  rating: number | null;
  message: string;
  surface: string;
  tags: string[];
  createdAt: string;
  repliedAt: string | null;
  user: AdminFeedbackUser | null;
}

export interface AdminFeedbackDetail extends AdminFeedbackRow {
  context: Record<string, unknown> | null;
  adminNote: string | null;
  repliedById: string | null;
  updatedAt: string;
}

export interface FeedbackKpis {
  newCount: number;
  detractorFollowupCount30d: number;
  exitSurveyCount30d: number;
  unansweredOlderThan48hCount: number;
}

export interface ListFeedbackQuery {
  status?: FeedbackStatus;
  kind?: FeedbackKind;
  surface?: string;
  ratingBucket?: RatingBucket;
  search?: string;
  from?: string;
  to?: string;
  take?: number;
  skip?: number;
}

export interface ListFeedbackResponse {
  rows: AdminFeedbackRow[];
  total: number;
}

export interface UpdateFeedbackInput {
  status?: FeedbackStatus;
  adminNote?: string | null;
  tags?: string[];
}

export interface ReplyFeedbackInput {
  subject: string;
  body: string;
  adminNote?: string;
}

export const FEEDBACK_KEYS = {
  list: (query: ListFeedbackQuery) => ["admin", "feedback", "list", query] as const,
  detail: (id: string) => ["admin", "feedback", "detail", id] as const,
  kpis: () => ["admin", "feedback", "kpis"] as const,
};

export const fetchAdminFeedback = async (
  query: ListFeedbackQuery = {},
): Promise<ListFeedbackResponse> => {
  const params: Record<string, string | number | undefined> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.length === 0) continue;
    params[key] = value as string | number;
  }
  const { data } = await axios.get<ListFeedbackResponse>("/admin/feedback", { params });
  return data;
};

export const fetchAdminFeedbackDetail = async (id: string): Promise<AdminFeedbackDetail> => {
  const { data } = await axios.get<AdminFeedbackDetail>(`/admin/feedback/${id}`);
  return data;
};

export const fetchAdminFeedbackKpis = async (): Promise<FeedbackKpis> => {
  const { data } = await axios.get<FeedbackKpis>("/admin/feedback/kpis");
  return data;
};

export const updateAdminFeedback = async (
  id: string,
  input: UpdateFeedbackInput,
): Promise<AdminFeedbackDetail> => {
  const { data } = await axios.patch<AdminFeedbackDetail>(`/admin/feedback/${id}`, input);
  return data;
};

export const replyAdminFeedback = async (
  id: string,
  input: ReplyFeedbackInput,
): Promise<AdminFeedbackDetail> => {
  const { data } = await axios.post<AdminFeedbackDetail>(`/admin/feedback/${id}/reply`, input);
  return data;
};

export const useAdminFeedbackList = (query: ListFeedbackQuery = {}) =>
  useQuery({
    queryKey: FEEDBACK_KEYS.list(query),
    queryFn: () => fetchAdminFeedback(query),
    staleTime: 10_000,
  });

export const useAdminFeedbackDetail = (id: string | undefined) =>
  useQuery({
    queryKey: FEEDBACK_KEYS.detail(id ?? ""),
    queryFn: () => fetchAdminFeedbackDetail(id as string),
    enabled: Boolean(id),
    staleTime: 10_000,
  });

export const useAdminFeedbackKpis = () =>
  useQuery({
    queryKey: FEEDBACK_KEYS.kpis(),
    queryFn: fetchAdminFeedbackKpis,
    // KPIs power both the dedicated feedback page and overview tiles — keep
    // them fresh-ish but cache long enough that a fast nav doesn't refetch.
    staleTime: 30_000,
  });

export const useUpdateAdminFeedback = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateFeedbackInput) => updateAdminFeedback(id, input),
    onSuccess: (data) => {
      qc.setQueryData(FEEDBACK_KEYS.detail(id), data);
      void qc.invalidateQueries({ queryKey: ["admin", "feedback"] });
    },
  });
};

export const useReplyAdminFeedback = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReplyFeedbackInput) => replyAdminFeedback(id, input),
    onSuccess: (data) => {
      qc.setQueryData(FEEDBACK_KEYS.detail(id), data);
      void qc.invalidateQueries({ queryKey: ["admin", "feedback"] });
    },
  });
};
